
import { Component, VModel, Prop, Mixins } from 'vue-property-decorator'

import MixinInterface from '@/_mixins/index'
import ObservationMixin from '@/_mixins/observation'

import { isWritable } from '@/utils/app'

import { openDialog } from '@/_components/dialog/dialog'
import AttachmentEditorDialog from '@/views/Attachment/dialogs/editor/editor.vue'
import Attachment from '@/components/Attachment/Attachment.vue'
import Editor from '@/components/Editor/Editor.vue'
import EditorViewer from '@/components/EditorViewer/EditorViewer.vue'

@Component({
    inheritAttrs: false,
    mixins: [ObservationMixin],
    components: { Attachment, Editor, EditorViewer },
})
export default class SelectedAttachment extends Mixins<MixinInterface>(ObservationMixin) {
    @VModel() selectedAttachment!: Record<string, any>
    @Prop({ required: true }) observation!: Record<string, any>
    @Prop({ default: false }) useAnonymousToken!: boolean

    isWritable = isWritable

    tools = [
        'header',
        'linkTool',
        'list',
        'embed',
        'quote',
        'table',
        'delimiter',
        'Marker',
        'underline',
    ]

    editorId = crypto['randomUUID']()
    get isInProgress() {
        return this.isAttachmentFileLoading || this.isDownloadOn || this.deleteAttachmentLoading
    }

    setAttachmentDescription(data: any) {
        if (!this.isEqualRichText(data, this.selectedAttachment.description))
            this.selectedAttachment.description = data
    }

    // Edit attachment file
    isAttachmentFileLoading = false
    updateError = false
    updatedFileObject = null as null | File //save edited file in case the post file failed and user wants to retry

    async openFileEdit() {
        if (!isWritable(this.selectedAttachment, 'fileId')) return

        this.isAttachmentFileLoading = true

        const baseFile = await this.downloadAttachmentAsFileObject(
            this.selectedAttachment.fileId,
            this.useAnonymousToken
        )
        this.isAttachmentFileLoading = false

        let res = await openDialog(AttachmentEditorDialog, {
            fileObject: baseFile.data,
            uploadingAttachment: this.isAttachmentFileLoading,
        })

        if (res instanceof File) await this.updateAttachment(res)
    }

    async updateAttachment(fileObject: File) {
        if (!isWritable(this.selectedAttachment, 'fileId')) return
        try {
            this.isAttachmentFileLoading = true
            const fileId = await this.postFile(fileObject, true, this.useAnonymousToken)
            if (fileId) this.selectedAttachment.fileId = fileId
        } catch (error) {
            this.updatedFileObject = fileObject
            this.updateError = true
        } finally {
            this.isAttachmentFileLoading = false
        }
    }

    // Download attachment
    isDownloadOn = false
    downloadError = false

    async downloadSelectedAttachment() {
        try {
            this.downloadError = false
            this.isDownloadOn = true
            await this.downloadAttachment(
                this.selectedAttachment.fileId,
                this.selectedAttachment.filename,
                this.useAnonymousToken
            )
        } catch (error) {
            this.downloadError = true
        } finally {
            this.isDownloadOn = false
        }
    }

    // Set as a main attachment
    get isMainAttachment() {
        let res = false

        if (this.selectedAttachment?.id) {
            if (this.observation.mainAttachment?.data?.type == 'attachment') {
                res = this.observation.mainAttachment.data.id == this.selectedAttachment.id
            }
        } else {
            if (this.observation.mainAttachment?.data?.type == 'file') {
                res = this.observation.mainAttachment.data.id == this.selectedAttachment.fileId
            }
        }
        return res
    }

    setAsMainAttachment(value: boolean) {
        if (!this.isWritable(this.observation, 'mainAttachment')) return

        this.$emit('mainAttachment:update', value ? this.selectedAttachment : null)
    }

    // Delete attachment
    deleteAttachmentLoading = false

    async deleteActiveAttachment() {
        if (this.selectedAttachment.canDelete === false) return
        try {
            this.deleteAttachmentLoading = true
            if (this.selectedAttachment.id)
                await this.deleteObservationAttachment(this.selectedAttachment.id)
            this.$emit('attachment:deleted', this.selectedAttachment)
        } finally {
            this.deleteAttachmentLoading = false
        }
    }
}
